import React, { useState, useEffect } from 'react';
import { 
  User as UserIcon, 
  MessageSquare, 
  Phone, 
  MapPin, 
  Star, 
  Award, 
  Search, 
  Filter,
  ChevronRight,
  Loader2,
  X,
  Send,
  CheckCircle2,
  Trash2,
  Reply
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  collection, 
  query, 
  onSnapshot, 
  addDoc, 
  serverTimestamp,
  orderBy,
  where,
  updateDoc,
  deleteDoc,
  doc
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { User } from 'firebase/auth';

interface Expert {
  id: string;
  name: string;
  email: string;
  specialty: string;
  location: string;
  rating: number;
  experience: string;
  photoUrl?: string;
  bio: string;
  phone?: string;
}

interface ExpertConnectProps {
  user: User | null;
  language: 'en' | 'te' | 'hi';
  isExpert?: boolean;
  isAdmin?: boolean;
}

export default function ExpertConnectScreen({ user, language, isExpert, isAdmin }: ExpertConnectProps) {
  const [experts, setExperts] = useState<Expert[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExpert, setSelectedExpert] = useState<Expert | null>(null);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [toast, setToast] = useState<{message: string, type: 'success' | 'error'} | null>(null);
  const [activeTab, setActiveTab] = useState<'experts' | 'messages'>('experts');
  const [expertInquiries, setExpertInquiries] = useState<any[]>([]);
  const [replyingTo, setReplyingTo] = useState<any | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);

  const t = {
    en: {
      title: 'Expert Connect',
      searchPlaceholder: 'Search experts by name or specialty...',
      experience: 'Experience',
      rating: 'Rating',
      contact: 'Contact Expert',
      messagePlaceholder: 'Type your question for the expert...',
      send: 'Send Message',
      messageSuccess: 'Message sent successfully! The expert will contact you soon.',
      noExperts: 'No experts found.',
      loading: 'Loading experts...',
      specialty: 'Specialty',
      bio: 'About',
      call: 'Call Now',
      location: 'Location',
      myInquiries: 'My Inquiries',
      noInquiries: 'No inquiries found.',
      expertReply: 'Expert Response:',
      manageExperts: 'Manage Experts',
      viewMessages: 'User Messages',
      expertMessagesTitle: 'User Inquiries',
      markDone: 'Mark Done',
      reply: 'Reply',
      delete: 'Delete',
      edit: 'Edit',
      cancel: 'Cancel'
    },
    te: {
      title: 'నిపుణుల సలహాలు',
      searchPlaceholder: 'పేరు లేదా నైపుణ్యం ద్వారా వెతకండి...',
      experience: 'అనుభవం',
      rating: 'రేటింగ్',
      contact: 'నిపుణుడిని సంప్రదించండి',
      messagePlaceholder: 'నిపుణుడి కోసం మీ ప్రశ్నను ఇక్కడ టైప్ చేయండి...',
      send: 'సందేశం పంపండి',
      messageSuccess: 'సందేశం విజయవంతంగా పంపబడింది! నిపుణుడు త్వరలో మిమ్మల్ని సంప్రదిస్తారు.',
      noExperts: 'నిపుణులు ఎవరూ లేరు.',
      loading: 'లోడ్ అవుతోంది...',
      specialty: 'నైపుణ్యం',
      bio: 'గురించి',
      call: 'కాల్ చేయండి',
      location: 'ప్రాంతం',
      myInquiries: 'నా ప్రశ్నలు',
      noInquiries: 'ప్రశ్నలు ఏవీ లేవు.',
      expertReply: 'నిపుణుడి సమాధానం:',
      manageExperts: 'నిపుణులు',
      viewMessages: 'యూజర్ సందేశాలు',
      expertMessagesTitle: 'యూజర్ ఇన్క్వైరీస్',
      markDone: 'పూర్తయింది',
      reply: 'సమాధానం',
      delete: 'తొలగించు',
      edit: 'సవరించు',
      cancel: 'రద్దు'
    },
    hi: {
      title: 'विशेषज्ञ से संपर्क करें',
      searchPlaceholder: 'नाम या विशेषता के आधार पर खोजें...',
      experience: 'अनुभव',
      rating: 'रेटिंग',
      contact: 'विशेषज्ञ से संपर्क करें',
      messagePlaceholder: 'विशेषज्ञ के लिए अपना प्रश्न यहाँ टाइप करें...',
      send: 'संदेश भेजें',
      messageSuccess: 'संदेश सफलतापूर्वक भेजा गया! विशेषज्ञ जल्द ही आपसे संपर्क करेंगे।',
      noExperts: 'कोई विशेषज्ञ नहीं मिला।',
      loading: 'लोड हो रहा है...',
      specialty: 'विशेषता',
      bio: 'के बारे में',
      call: 'अभी कॉल करें',
      location: 'स्थान',
      myInquiries: 'मेरी पूछताछ',
      noInquiries: 'कोई पूछताछ नहीं मिली।',
      expertReply: 'विशेषज्ञ की प्रतिक्रिया:',
      manageExperts: 'विशेषज्ञों का प्रबंधन करें',
      viewMessages: 'उपयोगकर्ता संदेश',
      expertMessagesTitle: 'उपयोगकर्ता पूछताछ',
      markDone: 'पूर्ण करें',
      reply: 'जवाब दें',
      delete: 'हटाएं',
      edit: 'संपादित करें',
      cancel: 'रद्द करें'
    }
  }[language];

  const [userMessages, setUserMessages] = useState<any[]>([]);

  useEffect(() => {
    if (!user) {
      setUserMessages([]);
      setExpertInquiries([]);
      return;
    }

    // User's own messages sent to experts
    const qUser = query(collection(db, 'expert_messages'), where('userId', '==', user.uid));
    const unsubUser = onSnapshot(qUser, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      docs.sort((a: any, b: any) => {
        const tA = a.timestamp?.seconds || 0;
        const tB = b.timestamp?.seconds || 0;
        return tB - tA;
      });
      setUserMessages(docs);
    });

    // Messages received by expert
    let unsubExpert = () => {};
    if (isExpert || isAdmin) {
      let qExpert = query(collection(db, 'expert_messages'), orderBy('timestamp', 'desc'));
      if (isExpert && !isAdmin && user.email) {
        const email = user.email.toLowerCase().trim();
        qExpert = query(collection(db, 'expert_messages'), where('expertEmail', '==', email), orderBy('timestamp', 'desc'));
      }
      unsubExpert = onSnapshot(qExpert, (snapshot) => {
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setExpertInquiries(docs);
      });
    }

    return () => {
      unsubUser();
      unsubExpert();
    };
  }, [user, isExpert, isAdmin]);

  useEffect(() => {
    const q = query(collection(db, 'experts'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (snapshot.empty) {
        // Seeding is disabled to prevent permission errors for non-admins.
        // Experts should be managed via the Admin Panel.
        setExperts([]);
      } else {
        setExperts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Expert)));
      }
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'experts');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !selectedExpert || !message.trim()) return;

    setIsSending(true);
    try {
      await addDoc(collection(db, 'expert_messages'), {
        expertId: selectedExpert.id,
        expertEmail: selectedExpert.email.toLowerCase().trim(),
        expertName: selectedExpert.name,
        userId: user.uid,
        userName: user.displayName || user.email,
        message: message.trim(),
        timestamp: serverTimestamp(),
        status: 'pending'
      });
      setMessage('');
      setToast({ message: t.messageSuccess, type: 'success' });
      setTimeout(() => setSelectedExpert(null), 2000);
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'expert_messages');
      setToast({ message: 'Failed to send message.', type: 'error' });
    } finally {
      setIsSending(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyingTo || !replyText.trim()) return;
    setIsSendingReply(true);
    try {
      await updateDoc(doc(db, 'expert_messages', replyingTo.id), {
        reply: replyText.trim(),
        replyTimestamp: serverTimestamp(),
        status: 'responded'
      });
      setReplyingTo(null);
      setReplyText('');
      setToast({ message: "Reply sent successfully", type: 'success' });
      setTimeout(() => setToast(null), 3000);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `expert_messages/${replyingTo.id}`);
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleMarkDone = async (msgId: string) => {
    try {
      await updateDoc(doc(db, 'expert_messages', msgId), { status: 'responded' });
      setToast({ message: "Marked as done", type: 'success' });
      setTimeout(() => setToast(null), 3000);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `expert_messages/${msgId}`);
    }
  };

  const handleDeleteMessage = async (msgId: string) => {
    try {
      await deleteDoc(doc(db, 'expert_messages', msgId));
      setToast({ message: "Message deleted", type: 'success' });
      setTimeout(() => setToast(null), 3000);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `expert_messages/${msgId}`);
    }
  };

  const filteredExperts = experts.filter(exp => 
    (exp.name || '').toLowerCase().includes((searchQuery || '').toLowerCase()) ||
    (exp.specialty || '').toLowerCase().includes((searchQuery || '').toLowerCase())
  );

  return (
    <div className="flex flex-col h-full bg-[#f8f9fa] overflow-hidden">
      {/* Header */}
      <div className="p-4 bg-white border-b border-black/5 space-y-4">
        <h2 className="text-xl font-bold text-[#1b7d36]">{t.title}</h2>
        
        {/* Tab System */}
        {(isExpert || isAdmin || userMessages.length > 0) && (
          <div className="flex gap-2 bg-stone-100 p-1 rounded-2xl">
            <button 
              onClick={() => setActiveTab('experts')}
              className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${activeTab === 'experts' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {isExpert || isAdmin ? t.manageExperts : t.title}
            </button>
            <button 
              onClick={() => setActiveTab('messages')}
              className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${activeTab === 'messages' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {t.viewMessages} {(isExpert || isAdmin) && expertInquiries.filter(m => m.status === 'pending').length > 0 && (
                <span className="bg-[#1b7d36] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center shadow-sm">
                  {expertInquiries.filter(m => m.status === 'pending').length}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Search bar maintained */}
        {activeTab === 'experts' && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <input 
              type="text" 
              placeholder={t.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#f1f3f5] border-none rounded-full py-2.5 pl-10 pr-4 text-sm focus:ring-2 focus:ring-[#1b7d36] outline-none"
            />
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-20">
        {activeTab === 'experts' ? (
          <>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 text-stone-400">
                <Loader2 className="animate-spin mb-2" size={32} />
                <p className="text-sm">{t.loading}</p>
              </div>
            ) : filteredExperts.length === 0 ? (
              <div className="text-center py-20 text-stone-400 italic">
                {t.noExperts}
              </div>
            ) : (
              filteredExperts.map(expert => (
                <motion.div
                  key={expert.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => setSelectedExpert(expert)}
                  className="bg-white rounded-[32px] p-5 shadow-sm border border-black/5 hover:border-[#1b7d36]/30 transition-all cursor-pointer flex gap-4"
                >
                  <div className="w-20 h-20 rounded-[24px] bg-stone-100 overflow-hidden shrink-0 border-2 border-white shadow-sm">
                    {expert.photoUrl ? (
                      <img src={expert.photoUrl} alt={expert.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300">
                        <UserIcon size={32} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-bold text-stone-900 truncate">{expert.name}</h3>
                      <div className="flex items-center gap-1 text-amber-500">
                        <Star size={14} className="fill-amber-500" />
                        <span className="text-xs font-bold">{expert.rating}</span>
                      </div>
                    </div>
                    <p className="text-xs font-bold text-[#1b7d36] mb-2">{expert.specialty}</p>
                    <div className="flex flex-wrap gap-3 text-[10px] text-stone-400 font-medium">
                      <div className="flex items-center gap-1">
                        <Award size={12} />
                        <span>{expert.experience}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin size={12} />
                        <span>{expert.location}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </>
        ) : (
          <div className="space-y-6">
            {/* View for Expert Management */}
            {(isExpert || isAdmin) && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-stone-800 px-2">{t.expertMessagesTitle}</h3>
                {expertInquiries.length === 0 ? (
                  <div className="text-center py-20 text-stone-400 italic bg-white rounded-[32px] border border-black/5">
                    {t.noInquiries}
                  </div>
                ) : (
                  expertInquiries.map((msg) => (
                    <motion.div 
                      key={msg.id} 
                      layout
                      className={`p-5 rounded-[32px] border shadow-sm transition-all ${msg.status === 'pending' ? 'bg-white border-emerald-100' : 'bg-stone-50 border-black/5 opacity-70'}`}
                    >
                      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-stone-800 flex items-center gap-2">
                            {msg.userName} 
                            {msg.status === 'pending' && <span className="w-2 h-2 bg-[#1b7d36] rounded-full animate-pulse" />}
                          </h4>
                          <p className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">
                            {isAdmin && `To: ${msg.expertName} • `}{msg.timestamp?.toDate?.()?.toLocaleString() || 'Just now'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button 
                            onClick={() => { setReplyingTo(msg); setReplyText(msg.reply || ''); }}
                            className="flex items-center gap-1 text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-100"
                          >
                            <Reply size={14} />
                            {msg.reply ? t.edit : t.reply}
                          </button>
                          {msg.status === 'pending' && (
                            <button 
                              onClick={() => handleMarkDone(msg.id)}
                              className="flex items-center gap-1 text-xs font-bold text-[#1b7d36] bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100"
                            >
                              <CheckCircle2 size={14} />
                              {t.markDone}
                            </button>
                          )}
                          <button 
                            onClick={() => handleDeleteMessage(msg.id)}
                            className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="text-sm text-stone-600 bg-stone-100/30 p-4 rounded-2xl border border-black/5 italic leading-relaxed">
                          "{msg.message}"
                        </div>
                        {msg.reply && (
                          <div className="bg-[#1b7d36]/5 p-4 rounded-2xl border border-[#1b7d36]/10">
                            <p className="text-[10px] font-bold text-[#1b7d36] uppercase tracking-wider mb-1">{t.expertReply}</p>
                            <p className="text-sm text-stone-700">{msg.reply}</p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            )}

            {/* User's Own Inquiries (if not redundant with management) */}
            {(!isExpert || isAdmin) && userMessages.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-stone-800 px-2 flex items-center gap-2">
                  <MessageSquare size={20} className="text-[#1b7d36]" />
                  {t.myInquiries}
                </h3>
                <div className="space-y-4">
                  {userMessages.map((msg) => (
                    <div key={msg.id} className="bg-white p-5 rounded-[32px] border border-black/5 shadow-sm space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">
                          To: {msg.expertName} • {msg.timestamp?.toDate?.()?.toLocaleDateString() || 'Just now'}
                        </div>
                        <div className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${msg.status === 'responded' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                          {msg.status === 'responded' ? 'REPLIED' : 'PENDING'}
                        </div>
                      </div>
                      <p className="text-sm text-stone-600 italic">"{msg.message}"</p>
                      {msg.reply && (
                        <div className="bg-emerald-50/50 p-4 rounded-2xl border border-emerald-100/50 space-y-1">
                          <p className="text-[10px] font-bold text-[#1b7d36] uppercase tracking-wider">{t.expertReply}</p>
                          <p className="text-sm text-stone-800 leading-relaxed">{msg.reply}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reply Modal */}
      <AnimatePresence>
        {replyingTo && (
          <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setReplyingTo(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              className="relative w-full max-w-lg bg-white rounded-t-[40px] sm:rounded-[40px] shadow-2xl overflow-hidden flex flex-col p-6 space-y-6"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-stone-800">{t.reply} to {replyingTo.userName}</h2>
                <button onClick={() => setReplyingTo(null)} className="p-2 hover:bg-stone-100 rounded-full">
                  <X size={24} />
                </button>
              </div>

              <div className="bg-stone-50 p-4 rounded-2xl border border-black/5 italic text-sm text-stone-600">
                "{replyingTo.message}"
              </div>

              <textarea 
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder="Type your response here..."
                className="w-full h-40 bg-white border border-stone-200 rounded-2xl p-4 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20 transition-all text-sm outline-none resize-none"
              />

              <div className="flex gap-3">
                <button 
                  onClick={handleSendReply}
                  disabled={isSendingReply || !replyText.trim()}
                  className="flex-1 bg-[#1b7d36] text-white py-4 rounded-2xl font-bold shadow-md hover:bg-[#16652b] disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                >
                  {isSendingReply && <Loader2 size={18} className="animate-spin" />}
                  {t.send}
                </button>
                <button onClick={() => setReplyingTo(null)} className="flex-1 bg-stone-100 text-stone-600 py-4 rounded-2xl font-bold">
                  {t.cancel}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Expert Detail Modal */}
      <AnimatePresence>
        {selectedExpert && (
          <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedExpert(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              className="relative w-full max-w-lg bg-white rounded-t-[40px] sm:rounded-[40px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              <div className="p-6 border-b flex items-center justify-between bg-[#1b7d36] text-white shrink-0">
                <h2 className="text-xl font-bold">{t.contact}</h2>
                <button onClick={() => setSelectedExpert(null)} className="p-1 hover:bg-white/10 rounded-full">
                  <X size={24} />
                </button>
              </div>

              <div className="overflow-y-auto flex-1 p-6 space-y-6">
                <div className="flex gap-6">
                  <div className="w-24 h-24 rounded-[32px] overflow-hidden border-4 border-emerald-50 shadow-md shrink-0">
                    {selectedExpert.photoUrl ? (
                      <img src={selectedExpert.photoUrl} alt={selectedExpert.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300 bg-stone-100">
                        <UserIcon size={40} />
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-bold text-stone-900">{selectedExpert.name}</h3>
                    <p className="text-sm font-bold text-[#1b7d36]">{selectedExpert.specialty}</p>
                    <div className="flex items-center gap-4 text-xs text-stone-500">
                      <div className="flex items-center gap-1">
                        <Award size={14} className="text-amber-500" />
                        <span>{selectedExpert.experience}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star size={14} className="text-amber-500 fill-amber-500" />
                        <span>{selectedExpert.rating}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider">{t.bio}</h4>
                  <p className="text-sm text-stone-600 leading-relaxed">{selectedExpert.bio}</p>
                </div>

                <div className="space-y-2">
                  <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider">{t.location}</h4>
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <MapPin size={16} className="text-[#1b7d36]" />
                    <span>{selectedExpert.location}</span>
                  </div>
                </div>

                <form onSubmit={handleSendMessage} className="space-y-4 pt-4 border-t">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-stone-400 uppercase tracking-wider">{t.contact}</label>
                    <textarea 
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t.messagePlaceholder}
                      rows={4}
                      className="w-full bg-stone-50 border border-stone-200 rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#1b7d36] outline-none resize-none"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={isSending || !message.trim() || !user}
                      className="flex-1 bg-[#1b7d36] text-white py-4 rounded-2xl font-bold shadow-lg hover:bg-[#144317] disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                    >
                      {isSending ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                      {t.send}
                    </button>
                    {selectedExpert.phone && (
                      <a 
                        href={`tel:${selectedExpert.phone}`}
                        className="p-4 bg-emerald-50 text-[#1b7d36] rounded-2xl border border-emerald-100 hover:bg-emerald-100 transition-colors"
                      >
                        <Phone size={24} />
                      </a>
                    )}
                  </div>
                  {!user && (
                    <p className="text-[10px] text-center text-stone-400 italic">Please login to send messages to experts.</p>
                  )}
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed bottom-24 left-4 right-4 p-4 rounded-2xl shadow-xl text-white font-bold text-sm flex items-center gap-3 z-[200] ${
              toast.type === 'success' ? 'bg-[#1b7d36]' : 'bg-rose-500'
            }`}
          >
            {toast.type === 'success' ? <Star size={20} /> : <X size={20} />}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
